import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Building2 } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Brands from "./Brands";
import SponsorFinder from "./SponsorFinder";

// Unified Brand Directory wrapper (Feb 2026 nav redesign).
// Merges the previous /app/sponsor-finder and /app/brands surfaces into a
// single page with two tabs. Default tab is AI-matched (the workflow we want
// athletes to start with). Tab choice is URL-synced so back-button works.

const TABS = [
  { key: "matches", label: "AI matches for me", icon: Sparkles,
    desc: "Brands the AI thinks fit your sport, audience and positioning." },
  { key: "all", label: "All brands", icon: Building2,
    desc: "Browse every brand on PITCHD with filters." },
];

export default function BrandDirectory() {
  const [search, setSearch] = useSearchParams();
  const urlTab = search.get("tab");
  const [tab, setTab] = useState(urlTab === "all" ? "all" : "matches");

  // Keep URL in sync with the active tab — sharable + browser-history friendly.
  useEffect(() => {
    if (search.get("tab") !== tab) {
      search.set("tab", tab);
      setSearch(search, { replace: true });
    }
  }, [tab, search, setSearch]);

  const active = TABS.find(t => t.key === tab) || TABS[0];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12 pb-0">
        <div className="label-eyebrow">Discover brands</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="brand-directory-title">
          Brand Directory
        </h1>
        <p className="text-sm text-ink-muted mt-3 max-w-2xl">{active.desc}</p>

        {/* Tab strip — pill-style buttons matching the editorial nav aesthetic */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-line" data-testid="brand-directory-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`brand-directory-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                  isActive ? "border-volt text-volt" : "border-transparent text-ink-muted hover:text-white"
                }`}>
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Render the matching child surface. Each child is a full page already
          (renders its own DashboardLayout normally) — we pass `embedded` so
          the child skips its layout wrapper when nested. */}
      <div data-testid={`brand-directory-pane-${tab}`}>
        {tab === "matches" ? <SponsorFinder embedded /> : <Brands embedded />}
      </div>
    </DashboardLayout>
  );
}
